import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import ScrollContainer from "react-indiana-drag-scroll";
import { bucketUrlPrefix } from "common/config";
import { useAuth, useI18N } from "common/hooks";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import NoSearchResults from "@/assets/images/notfound.svg";
import "react-medium-image-zoom/dist/styles.css";
import Loader from "@/components/common/Loader";
import { Helmet } from "react-helmet-async";
import FlagVenuePhoto from "@/features/flag-report/FlagVenuePhoto";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
  actionToPerformOwnerAtom,
} from "@/store/atoms";
import { ArrowLeftIcon, Maximize2 } from "lucide-react";
// import { ArrowsDownUp } from"phosphor-react";
import { trackEvent } from "common/analytics";
import { getCurrentPageKey } from "@/analytics/pageKey";

// Photos-tab collection toggle → photo_collection_type (vocab: walkthrough / by_users; "experiences"
// is an extra web tab → emitted as-is, flagged for lexicon).
const PHOTO_COLLECTION_TYPE_BY_TAB: Record<string, string> = {
  "by-sociability": "walkthrough",
  "by-users": "by_users",
  experiences: "experiences",
};
import FullImageViewer from "@/components/FullImageViewer";
import { FolderDottedIcon } from "@phosphor-icons/react";

// VenuePhotosOptions type is now defined in the route file

// We'll extract display sections from the API data

// Define types for hybrid venue structure
interface TagOption {
  optionTitle: string;
  optionTitleId?: number;
  category_display_section_id?: string;
}

interface QuestionOption {
  tagOptions?: TagOption[];
  category_display_section_id?: string;
  category_display_section_name?: string;
  title?: string;
  titleId?: number;
}

interface DisplaySection {
  id: number;
  category_id: number;
  name: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface LevelOption {
  levelPhotos?: Array<{
    levelName?: string;
    sort_order?: number;
    width?: number;
    height?: number;
    [key: string]: any;
  }>;
  questionOptions?: QuestionOption[];
  questionDisplaySections?: DisplaySection[];
  name?: string;
  levelName?: string;
  level?: string;
}

// Define types for the photo data structure
interface PhotoItem {
  path: string;
  alt_text?: string;
  category_name: string;
  category_id: number;
  tag_name: string;
  created_at: string;
  photo_type?: string;
  review_photos_id?: string;
  width?: number;
  height?: number;
  sort_order?: number;
  // Venue type flag to determine rendering behavior
  venueType?: "Basic" | "Hybrid";
  // Accessibility tag properties
  qtags?: Array<{
    tagId: number;
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  tags?: Array<{
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  // Display sections for accessibility tags
  displaySections?: Record<string, string>;
  // UserIcon roles for filtering
  user_roles?: {
    isGeneralUser: boolean;
  };
  approved_for_public?: boolean;
}

interface CategoryGroup {
  [subcategory: string]: PhotoItem[];
}

interface PhotoCategories {
  [category: string]: CategoryGroup;
}

// Note: Route definition is now in /routes/venuephotos.$venueSlug.tsx

// Function to sort photos based on the selected sort option
const sortPhotos = (photos: PhotoItem[], sortOption: string): PhotoItem[] => {
  // Create a copy of the photos array to avoid mutating the original
  const sortedPhotos = [...photos];

  switch (sortOption) {
    case "latest":
      // Sort by created_at date in descending order (newest first)
      return sortedPhotos.sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

    case "earliest":
      // Sort by created_at date in ascending order (oldest first)
      return sortedPhotos.sort((a, b) => {
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });

    case "category":
      // For category sorting, sort by category name, then subcategory name, then sort_order
      return sortedPhotos.sort((a, b) => {
        // First compare by category name
        const categoryComparison = a.category_id - b.category_id;
        if (categoryComparison !== 0) return categoryComparison;

        // If categories are the same, compare by subcategory/tag
        const subcategoryComparison = a.sort_order - b.sort_order;
        if (subcategoryComparison !== 0) return subcategoryComparison;

        // If both category and subcategory are the same, use sort_order
        return (a.sort_order || 0) - (b.sort_order || 0);
      });

    default:
      // Default to the original API order using sort_order field only
      return sortedPhotos.sort((a, b) => {
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
  }
};

export interface VenuePhotosContentProps {
  venueDetailsResponse: any;
  sort: "latest" | "earliest" | "category";
  area?: string;
  initialFilterArea?: string;
  isLoading: boolean;
  onRefresh: () => void;
  onFilterChange?: (filter: string) => void;
  onSortChange?: (sort: "latest" | "earliest" | "category") => void;
  onFlag?: (image: PhotoItem) => void;
  onBack?: () => void;
  mode?: string;
  cid?: string;
  tid?: string;
  doNotShowBackButton?: boolean | false;
  onShowPhotosDetailView?: (initialCategoryId: number | null) => void;
}

function VenuePhotosContent({
  venueDetailsResponse: data,
  sort,
  isLoading,
  onRefresh: loadVenueDetails,
  mode,
  cid,
  tid,
  doNotShowBackButton = false,
  onShowPhotosDetailView,
}: VenuePhotosContentProps) {
  const routeSlug = data?.details?.business?.slug || "";

  const i18n = useI18N();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom<any>(actionToPerformValueAfterLoginAtom);
  const [actionToPerformOwner, setActionToPerformOwner] = useAtom(
    actionToPerformOwnerAtom,
  );
  // Distinct owner id: the basic-sheet photo tab. Keeps this surface's resume
  // from firing for a photo flag started in the full-screen PhotosDetailView.
  const FLAG_OWNER = "photo:sheet";

  const [fullImageView, setFullImageView] = useState(false);
  const [currentImage, setCurrentImage] = useState<PhotoItem | null>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  useEffect(() => setPortalRoot(document.body), []);
  const [isFlagVenuePhotoOpen, setIsFlagVenuePhotoOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<PhotoItem | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("");
  const [cidSetFilter, setCidSetFilter] = useState<string>(""); // Track if filter was set by cid
  const [tidSetFilter, setTidSetFilter] = useState<string>(""); // Track if filter was set by tid
  const [activeTab, setActiveTab] = useState<string>("by-sociability");
  const [hasSociabilityPhotos, setHasSociabilityPhotos] = useState(false);
  const [hasUserPhotos, setHasUserPhotos] = useState(false);
  const [sociabilityPhotoCount, setSociabilityPhotoCount] = useState(0);
  const [userPhotoCount, setUserPhotoCount] = useState(0);
  const [defaultTabSet, setDefaultTabSet] = useState(false);
  const [isExperienceViewerOpen, setIsExperienceViewerOpen] = useState(false);
  const [currentExperienceViewerIndex, setCurrentExperienceViewerIndex] = useState(0);

  // Experience photos derived from business reviews (reviews that have reviewPhotos)
  const experiencePhotosList = useMemo(() => {
    const reviewsWithPhotos = (data?.details?.businessReviews ?? []).filter(
      (review) => (review.reviewPhotos?.length ?? 0) > 0,
    );
    return reviewsWithPhotos.flatMap((review) =>
      (review.reviewPhotos ?? []).map((photo) => ({
        ...photo,
        review,
        path: photo.path || photo.path_thumb || photo.pathThumb || "",
        width: photo.width ?? 1200,
        height: photo.height ?? 900,
        alt_text: photo.alt_text ?? photo.altText ?? "",
      })),
    );
  }, [data?.details?.businessReviews]);

  const hasExperiencePhotos = experiencePhotosList.length > 0;

  // Refs for scroll spy functionality
  const isProgrammaticScroll = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // Scroll to category section with offset for better positioning
  const scrollToCategory = (categoryName: string) => {
    isProgrammaticScroll.current = true;
    // Find the category heading element directly
    const categoryHeading = document.querySelector(
      `[data-category-heading="${categoryName}"]`,
    ) as HTMLElement;
    if (!categoryHeading) return;

    // Try to find the scrollable container - check multiple possible selectors
    let scrollContainer = document.querySelector(
      ".flex-1.overflow-y-auto",
    ) as HTMLElement;

    // If not found, try to find the main scrollable area
    if (!scrollContainer) {
      scrollContainer = document.querySelector(
        "main.overflow-y-auto",
      ) as HTMLElement;
    }

    // Additional check: ensure the container actually has scrollable content
    if (
      scrollContainer &&
      scrollContainer.scrollHeight > scrollContainer.clientHeight
    ) {
      // Get the position of the heading relative to the scroll container
      const containerRect = scrollContainer.getBoundingClientRect();
      const headingRect = categoryHeading.getBoundingClientRect();

      // Calculate the scroll position with offset
      // Use smaller offset on mobile for better visibility
      const isMobile = window.innerWidth < 768;
      const offset = isMobile ? 80 : 120;
      const scrollTop =
        scrollContainer.scrollTop +
        (headingRect.top - containerRect.top) -
        offset;

      // Scroll to the calculated position
      scrollContainer.scrollTo({
        top: Math.max(0, scrollTop), // Ensure we don't scroll to negative position
        behavior: "smooth",
      });
    } else {
      // Fallback: scroll using the element's scrollIntoView method
      // This works more reliably across different layouts and devices
      const isMobile = window.innerWidth < 768;
      const offset = isMobile ? 80 : 120;

      // Use scrollIntoView with block:'start' for better cross-device support
      categoryHeading.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Apply offset after scrollIntoView completes (workaround for offset)
      setTimeout(() => {
        const scrollableParent = scrollContainer || window;
        if (scrollableParent === window) {
          window.scrollBy({
            top: -offset,
            behavior: "smooth",
          });
        } else {
          scrollableParent.scrollBy({
            top: -offset,
            behavior: "smooth",
          });
        }
      }, 100);
    }

    // Move screen reader focus to the category heading and reset programmatic scroll flag
    setTimeout(() => {
      categoryHeading.focus();
      isProgrammaticScroll.current = false;
    }, 1000);
  };

  // const [activeSubcategory, setActiveSubcategory] = useState<string>(""); // Commented out - not used when sidebar is hidden
  // Create a ref object to store subcategory refs
  const subcategoryRefs = useRef<
    Record<string, Record<string, HTMLDivElement>>
  >({});
  // Add ref for photo grid buttons to manage focus
  const photoGridRefs = useRef<
    Record<string, Record<string, HTMLButtonElement>>
  >({});
  // Create refs for category sections for better scrolling
  const categoryRefs = useRef<Record<string, HTMLDivElement>>({});
  // const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  // const [sidebarOpen, setSidebarOpen] = useState(false); // Commented out - not used when sidebar is hidden
  const [venueSlug, setVenueSlug] = useState(routeSlug);

  const { user, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const error = data?.error;

  // Define function to reload photos (used in useEffect and other places)
  const loadPhotos = React.useCallback(() => {
    loadVenueDetails();
  }, [loadVenueDetails]);

  const [reviewPhotos, setReviewPhotos] = useState<PhotoItem[]>([]);
  const [photosByCategory, setPhotosByCategory] = useState<PhotoCategories>({});

  useEffect(() => {
    if (
      isLoggedIn &&
      user &&
      actionToPerformAfterLogin === "flagPhoto" &&
      actionToPerformOwner === FLAG_OWNER
    ) {
      setSelectedImage(actionToPerformValueAfterLogin);
      setIsFlagVenuePhotoOpen(true);
      setActionToPerformAfterLogin("");
      setActionToPerformValueAfterLogin("");
      setActionToPerformOwner("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, actionToPerformOwner, user, isLoggedIn]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (reviewPhotos.length === 0) {
      loadPhotos();
    }
  }, [loadPhotos, reviewPhotos.length]);

  // Store category images for use in the sidebar - commented out when sidebar is hidden
  // const [categoryImages, setCategoryImages] = useState<Record<string, { light: string, dark: string }>>({});

  // Set first category as selected by default when categories are loaded
  useEffect(() => {
    const categoriesWithContent = Object.entries(photosByCategory)
      .filter(([, subcategories]) => {
        const subcategoryEntries = Object.entries(subcategories);
        if (subcategoryEntries.length === 0) return false;
        return subcategoryEntries.some(([, photos]) => photos.length > 0);
      })
      .map(([categoryName]) => categoryName);

    if (categoriesWithContent.length > 0 && !activeFilter) {
      setActiveFilter(categoriesWithContent[0]);
    }
  }, [photosByCategory, activeFilter]);

  // Handle cid navigation - similar to AccessibilitySection.tsx
  useEffect(() => {
    if (
      cid &&
      data?.accesibilityCategories &&
      data.accesibilityCategories.length > 0
    ) {
      const category = data.accesibilityCategories.find(
        (cat) => cat.catId === parseInt(cid),
      );
      if (category) {
        setActiveFilter(category.catName);
        setCidSetFilter(category.catName); // Mark that this was set by cid

        // Handle tid scrolling if provided
        if (tid) {
          let targetSubcategory = null;
          category.tags?.forEach((tagGroup) => {
            if (tagGroup.qtags && Array.isArray(tagGroup.qtags)) {
              const matchingTag = tagGroup.qtags.find(
                (qtag) => qtag.tagId.toString() === tid.toString(),
              );
              if (matchingTag) {
                targetSubcategory = matchingTag.tagName;
                setTidSetFilter(matchingTag.tagName);
              }
            }
          });

          // Scroll to subcategory if found
          if (targetSubcategory) {
            setTimeout(() => {
              const subcategoryHeading = document.querySelector(
                `[data-subcategory-heading="${category.catName}:${targetSubcategory}"]`,
              ) as HTMLElement;
              if (subcategoryHeading) {
                subcategoryHeading.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }, 100);
          } else {
            // If no subcategory, scroll to the category
            setTimeout(() => scrollToCategory(category.catName), 100);
          }
        }
      }
    } else if (
      cid &&
      data?.accesibilityCategoriesHybrid &&
      data.accesibilityCategoriesHybrid.length > 0
    ) {
      const category = data.accesibilityCategoriesHybrid.find(
        (cat) => cat.catId === parseInt(cid),
      );
      if (category) {
        setActiveFilter(category.catName);
        setCidSetFilter(category.catName); // Mark that this was set by cid

        // Handle tid scrolling for hybrid venues if provided
        if (tid) {
          let targetSubcategory = null;
          category.categoryOptions?.forEach((categoryOption) => {
            categoryOption.pageChildrenCategories?.forEach((pageCategory) => {
              pageCategory.accessibilityOptions?.forEach(
                (accessibilityOption) => {
                  accessibilityOption.levelOptions?.forEach((level) => {
                    level.questionOptions?.forEach((question) => {
                      const tagOptions = Array.isArray(question.tagOptions)
                        ? question.tagOptions
                        : [];
                      const matchingTag = tagOptions.find(
                        (tag) =>
                          tag.optionTitleId &&
                          tag.optionTitleId.toString() === tid.toString(),
                      );
                      if (matchingTag) {
                        targetSubcategory = matchingTag.optionTitle;
                        setTidSetFilter(matchingTag.optionTitle);
                      }
                    });
                  });
                },
              );
            });
          });

          // Scroll to subcategory if found
          if (targetSubcategory) {
            setTimeout(() => {
              const subcategoryHeading = document.querySelector(
                `[data-subcategory-heading="${category.catName}:${targetSubcategory}"]`,
              ) as HTMLElement;
              if (subcategoryHeading) {
                subcategoryHeading.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }, 100);
          } else {
            // If no subcategory, scroll to the category
            setTimeout(() => scrollToCategory(category.catName), 100);
          }
        }
      }
    }
  }, [
    cid,
    tid,
    data?.accesibilityCategories,
    data?.accesibilityCategoriesHybrid,
  ]);

  // Organize photos by category and subcategory
  useEffect(() => {
    if (!data) return;

    const enrichAndGroupPhotos = async (activeTabFilter: string) => {
      // Indoor, Outdoor and VSH categories IDs
      const vshIndoorCatId = 96;
      const vshOutdoorCatId = 216;
      const regularIndoorCatId = 58;
      const regularOutdoorCatId = 172;

      // Extract photos from review_tags
      const allPhotos = [];
      const catImages: Record<string, { light: string; dark: string }> = {};
      const displaySections: Record<string, string> = {};

      // Create a set to collect all category names, even those without photos
      const allCategoryNames = new Set<string>();
      // Create a map to track category IDs by name
      const categoryIdMap = new Map<string, number>();
      // Track subcategories by category ID for special VSH handling
      // const subcategoriesByCatId = new Map<number, string[]>();

      // Loop through all categories for Basic and Hybris venues
      if (
        data?.details?.templateType !== "Basic" &&
        data?.details?.isPublicTemplate === "Yes" &&
        Array.isArray(data.accesibilityCategoriesHybrid)
      ) {
        // Hybrid venue
        // Step 1: Collect known sub-category names from hybrid indoor/outdoor categories
        const hybridKnownSubCategoryNames = new Set();

        data.accesibilityCategoriesHybrid.forEach((category) => {
          if (
            [regularIndoorCatId, regularOutdoorCatId].includes(category.catId)
          ) {
            category.categoryOptions?.forEach((categoryOption) => {
              categoryOption.pageChildrenCategories?.forEach((pageCategory) => {
                pageCategory.accessibilityOptions?.forEach(
                  (accessibilityOption) => {
                    accessibilityOption.levelOptions?.forEach((level) => {
                      if (level.level) {
                        hybridKnownSubCategoryNames.add(level.level.trim());
                      }
                    });
                  },
                );
              });
            });
          }
        });

        // Step 2: Build VSH tag maps for hybrid structure
        const hybridVSHIndoorQTagsBySubCat = new Map();
        const hybridVSHOutdoorQTagsBySubCat = new Map();

        data.accesibilityCategoriesHybrid.forEach((category) => {
          if ([vshIndoorCatId, vshOutdoorCatId].includes(category.catId)) {
            const mapToUse =
              category.catId === vshIndoorCatId
                ? hybridVSHIndoorQTagsBySubCat
                : hybridVSHOutdoorQTagsBySubCat;

            category.categoryOptions?.forEach((categoryOption) => {
              categoryOption.pageChildrenCategories?.forEach((pageCategory) => {
                pageCategory.accessibilityOptions?.forEach(
                  (accessibilityOption) => {
                    accessibilityOption.levelOptions?.forEach((level) => {
                      const subCatName = level.level?.trim();
                      if (
                        !subCatName ||
                        !hybridKnownSubCategoryNames.has(subCatName)
                      )
                        return;

                      const vshQtags: any[] = [];

                      level.questionOptions?.forEach((question) => {
                        const tagOptions = Array.isArray(question.tagOptions)
                          ? question.tagOptions
                          : [];
                        tagOptions.forEach((tag) => {
                          // Add type assertion to fix TypeScript errors
                          const typedTag = tag as TagOption;
                          if (
                            typedTag.optionTitle &&
                            typedTag.optionTitle !== subCatName
                          ) {
                            // Add type assertion for question object
                            const typedQuestion =
                              question as unknown as QuestionOption;
                            vshQtags.push({
                              tagId: typedTag.optionTitleId || 0,
                              tagName: typedTag.optionTitle,
                              categoryDisplaySectionId:
                                typedQuestion.category_display_section_id || "",
                            });
                          }
                        });
                      });

                      const existing = mapToUse.get(subCatName) || [];
                      mapToUse.set(subCatName, existing.concat(vshQtags));
                    });
                  },
                );
              });
            });
          }
        });

        // Step 3: Process Hybrid venue photos with merged VSH tags
        data.accesibilityCategoriesHybrid.forEach((category) => {
          if (category.catName) {
            // Add to all category names set
            allCategoryNames.add(category.catName);
            // Store category ID by name
            if (category.catId) {
              categoryIdMap.set(category.catName, category.catId);
            }

            catImages[category.catName] = {
              light: category.catImage || "",
              dark: category.catImageDark || "",
            };
          }

          category.categoryOptions?.forEach((categoryOption) => {
            categoryOption.pageChildrenCategories?.forEach((pageCategory) => {
              pageCategory.accessibilityOptions?.forEach(
                (accessibilityOption) => {
                  accessibilityOption.levelOptions?.forEach((level) => {
                    // Type-safe handling of level
                    const safeLevel = level as unknown as LevelOption;
                    // Extract display sections if available
                    if (Array.isArray(safeLevel.questionDisplaySections)) {
                      // Add display sections to our map
                      safeLevel.questionDisplaySections.forEach((section) => {
                        if (section.id && section.name) {
                          displaySections[section.id.toString()] = section.name;
                        }
                      });
                    }

                    // Process tags for hybrid venues
                    const subCatName = safeLevel.level?.trim() || "";
                    let qtags = [];

                    // Extract tags from questionOptions -> tagOptions
                    safeLevel.questionOptions?.forEach(
                      (question: QuestionOption) => {
                        // Safe access to tagOptions with type checking
                        const tagOptions = Array.isArray(question.tagOptions)
                          ? question.tagOptions
                          : [];

                        tagOptions.forEach((tag: TagOption) => {
                          // Handle different property structures in hybrid venues using type assertion
                          const tagName = tag.optionTitle || "";
                          if (tagName) {
                            qtags.push({
                              tagId: tag.optionTitleId || 0,
                              tagName: tagName,
                              categoryDisplaySectionId:
                                question.category_display_section_id || "",
                            });
                          }
                        });
                      },
                    );

                    // Merge in VSH qtags if sub-category matches
                    if (
                      category.catId === regularIndoorCatId &&
                      hybridVSHIndoorQTagsBySubCat.has(subCatName)
                    ) {
                      qtags = qtags.concat(
                        hybridVSHIndoorQTagsBySubCat.get(subCatName),
                      );
                    } else if (
                      category.catId === regularOutdoorCatId &&
                      hybridVSHOutdoorQTagsBySubCat.has(subCatName)
                    ) {
                      qtags = qtags.concat(
                        hybridVSHOutdoorQTagsBySubCat.get(subCatName),
                      );
                    }

                    // Create tags array from level name as fallback
                    const tags = [
                      {
                        tagName:
                          (safeLevel as any).name ||
                          (safeLevel as any).levelName ||
                          "Uncategorized",
                        categoryDisplaySectionId: "",
                      },
                    ];

                    safeLevel.levelPhotos?.forEach((photo) => {
                      allPhotos.push({
                        ...photo,
                        category_name: category.catName || "Uncategorized",
                        tag_name: photo.levelName,
                        category_id: category.catId || 0,
                        sort_order: photo.sort_order || 0,
                        width: photo.width || 800,
                        height: photo.height || 600,
                        venueType: "Hybrid",
                        qtags: qtags,
                        tags: tags,
                        displaySections: displaySections,
                        user_roles: photo.user_roles || {
                          isGeneralUser: false,
                        },
                      });
                    });
                  });
                },
              );
            });
          });
        });
      } else {
        // Basic venue
        // Step 1: Collect all known sub-category names from regular categories
        const knownSubCategoryNames = new Set();
        data.accesibilityCategories.forEach((category) => {
          if (
            [regularIndoorCatId, regularOutdoorCatId].includes(category.catId)
          ) {
            category.tags?.forEach((tagGroup) => {
              tagGroup.reviewPhotos?.forEach((photo) => {
                if (photo.tag_name) {
                  knownSubCategoryNames.add(photo.tag_name.trim());
                }
              });
            });
          }
        });

        // Step 2: Build VSH sub-category â†’ tags map
        const vshIndoorQTagsBySubCat = new Map();
        const vshOutdoorQTagsBySubCat = new Map();

        data?.accesibilityCategories?.forEach((category) => {
          if ([vshIndoorCatId, vshOutdoorCatId].includes(category.catId)) {
            category.tags?.forEach((tagGroup) => {
              const qtags = tagGroup.qtags || [];
              const mapToUse =
                category.catId === vshIndoorCatId
                  ? vshIndoorQTagsBySubCat
                  : vshOutdoorQTagsBySubCat;

              qtags.forEach((currentTag) => {
                const subCatName = currentTag.tagName?.trim();
                if (!subCatName || !knownSubCategoryNames.has(subCatName))
                  return;

                const otherTags = qtags.filter((t) => t.tagName !== subCatName);
                const existing = mapToUse.get(subCatName) || [];
                mapToUse.set(subCatName, existing.concat(otherTags));
              });
            });
          }
        });

        // Step 3: Process all categories and assign photos
        if (
          data?.accesibilityCategories &&
          Array.isArray(data.accesibilityCategories)
        ) {
          data.accesibilityCategories.forEach((category) => {
            if (category.catName) {
              // Add to all category names set
              allCategoryNames.add(category.catName);
              // Store category ID by name
              if (category.catId) {
                categoryIdMap.set(category.catName, category.catId);
              }

              catImages[category.catName] = {
                light: category.catImage || "",
                dark: category.catImageDark || "",
              };
            }

            // Extract display sections if available
            if (
              category.cat_display_sections &&
              typeof category.cat_display_sections === "object"
            ) {
              // Add display sections to our map
              Object.entries(category.cat_display_sections).forEach(
                ([key, value]) => {
                  if (typeof value === "string") {
                    displaySections[key] = value;
                  }
                },
              );
            }

            if (category.tags && Array.isArray(category.tags)) {
              category.tags.forEach((tagGroup) => {
                if (
                  tagGroup.reviewPhotos &&
                  Array.isArray(tagGroup.reviewPhotos)
                ) {
                  tagGroup.reviewPhotos.forEach((photo) => {
                    const subCatName = photo.tag_name?.trim() || "";
                    // Extract qtags from the tagGroup if available
                    // For basic venues, we'll use the tagGroup's qtags directly if available
                    let qtags =
                      tagGroup.qtags && Array.isArray(tagGroup.qtags)
                        ? tagGroup.qtags.map((qtag) => ({
                            tagId: qtag.tagId || 0,
                            tagName: qtag.tagName || "",
                            categoryDisplaySectionId:
                              qtag.categoryDisplaySectionId || "",
                          }))
                        : [];

                    // Merge in VSH tags if sub-category matches
                    if (
                      category.catId === regularIndoorCatId &&
                      vshIndoorQTagsBySubCat.has(subCatName)
                    ) {
                      qtags = qtags.concat(
                        vshIndoorQTagsBySubCat.get(subCatName),
                      );
                    } else if (
                      category.catId === regularOutdoorCatId &&
                      vshOutdoorQTagsBySubCat.has(subCatName)
                    ) {
                      qtags = qtags.concat(
                        vshOutdoorQTagsBySubCat.get(subCatName),
                      );
                    }

                    // We'll create tags from the tag_name as a fallback
                    const tags = [
                      {
                        tagName: photo.tag_name || "Uncategorized",
                        categoryDisplaySectionId: "",
                      },
                    ];

                    allPhotos.push({
                      ...photo,
                      category_name: category.catName || "Uncategorized",
                      tag_name: photo.tag_name || "Uncategorized",
                      category_id: category.catId || 0,
                      sort_order: tagGroup.sort_order || 0,
                      width: photo.width || 800,
                      height: photo.height || 600,
                      venueType: "Basic",
                      qtags: qtags,
                      tags: tags,
                      displaySections: displaySections,
                      user_roles: photo.user_roles || {
                        isGeneralUser: false,
                      },
                    });
                  });
                }
              });
            }
          });
        }
      }

      // Set the category images for use in the sidebar - commented out when sidebar is hidden
      // setCategoryImages(catImages);

      // console.log("allPhotos.length:", allPhotos.length);

      // CheckIcon photo availability by user role
      // Walk-through: mapper/team photos + approved general-user photos
      const sociabilityPhotos = allPhotos.filter(
        (photo) =>
          photo.user_roles?.isGeneralUser !== true ||
          photo.approved_for_public === true,
      );
      // By Users: only non-approved general-user photos
      const userPhotos = allPhotos.filter(
        (photo) =>
          photo.user_roles?.isGeneralUser === true &&
          photo.approved_for_public !== true,
      );

      setHasSociabilityPhotos(sociabilityPhotos.length > 0);
      setHasUserPhotos(userPhotos.length > 0);
      setSociabilityPhotoCount(sociabilityPhotos.length);
      setUserPhotoCount(userPhotos.length);

      // Set default active tab based on photo availability (only once)
      if (!defaultTabSet) {
        if (sociabilityPhotos.length > 0) {
          setActiveTab("by-sociability");
        } else if (userPhotos.length > 0) {
          setActiveTab("by-users");
        }
        setDefaultTabSet(true);
      }


      // Filter photos based on user role tab
      const filteredAllPhotos = (() => {
        if (activeTabFilter === "by-sociability") {
          // Walk-through: mapper/team photos + approved general-user photos
          return allPhotos.filter(
            (photo) =>
              photo.user_roles?.isGeneralUser !== true ||
              photo.approved_for_public === true,
          );
        } else if (activeTabFilter === "by-users") {
          // By Users: only non-approved general-user photos
          return allPhotos.filter(
            (photo) =>
              photo.user_roles?.isGeneralUser === true &&
              photo.approved_for_public !== true,
          );
        }
        return allPhotos; // Return all photos if no valid tab
      })();

      // Sort the photos based on the selected sort option
      const sortedPhotos = sortPhotos(filteredAllPhotos, sort);

      setReviewPhotos(sortedPhotos);

      // Step 2: Group photos by category and subcategory
      const photoGroups: PhotoCategories = {};
      const uniqueCategories: Set<string> = new Set();

      // First, initialize photoGroups with all categories, even those without photos
      allCategoryNames.forEach((categoryName) => {
        uniqueCategories.add(categoryName);
        photoGroups[categoryName] = {};
      });

      // Then add photos to their categories and subcategories
      sortedPhotos.forEach((photo) => {
        const categoryName = photo.category_name || "Uncategorized";
        const subcategoryName = photo.tag_name || "Uncategorized";
        uniqueCategories.add(categoryName);

        if (!photoGroups[categoryName]) {
          photoGroups[categoryName] = {};
        }
        if (!photoGroups[categoryName][subcategoryName]) {
          photoGroups[categoryName][subcategoryName] = [];
        }
        photoGroups[categoryName][subcategoryName].push(photo);
      });

      // Special handling for VSH categories - inherit subcategories from regular categories
      // Find category names corresponding to the IDs
      let vshIndoorCategoryName = "";
      let vshOutdoorCategoryName = "";
      let regularIndoorCategoryName = "";
      let regularOutdoorCategoryName = "";

      // Map category names to their IDs
      categoryIdMap.forEach((id, name) => {
        if (id === vshIndoorCatId) vshIndoorCategoryName = name;
        if (id === vshOutdoorCatId) vshOutdoorCategoryName = name;
        if (id === regularIndoorCatId) regularIndoorCategoryName = name;
        if (id === regularOutdoorCatId) regularOutdoorCategoryName = name;
      });

      // Copy subcategories from regular Indoor to VSH Indoor
      if (
        vshIndoorCategoryName &&
        regularIndoorCategoryName &&
        photoGroups[regularIndoorCategoryName]
      ) {
        // CheckIcon if regular Indoor has any subcategories
        const regularIndoorSubcategories = Object.keys(
          photoGroups[regularIndoorCategoryName],
        );

        if (regularIndoorSubcategories.length > 0) {
          // Make sure VSH Indoor category exists
          if (!photoGroups[vshIndoorCategoryName]) {
            photoGroups[vshIndoorCategoryName] = {};
          }

          // Copy all subcategories from regular Indoor to VSH Indoor
          regularIndoorSubcategories.forEach((subcategoryName) => {
            // Only add subcategories that don't already exist
            if (!photoGroups[vshIndoorCategoryName][subcategoryName]) {
              photoGroups[vshIndoorCategoryName][subcategoryName] = [];
            }
          });
        } else {
          // If regular Indoor has no subcategories, remove VSH Indoor category
          if (photoGroups[vshIndoorCategoryName]) {
            delete photoGroups[vshIndoorCategoryName];
            // Remove from allCategoryNames if it exists
            allCategoryNames.delete(vshIndoorCategoryName);
          }
        }
      }

      // Copy subcategories from regular Outdoor to VSH Outdoor
      if (
        vshOutdoorCategoryName &&
        regularOutdoorCategoryName &&
        photoGroups[regularOutdoorCategoryName]
      ) {
        // CheckIcon if regular Outdoor has any subcategories
        const regularOutdoorSubcategories = Object.keys(
          photoGroups[regularOutdoorCategoryName],
        );

        if (regularOutdoorSubcategories.length > 0) {
          // Make sure VSH Outdoor category exists
          if (!photoGroups[vshOutdoorCategoryName]) {
            photoGroups[vshOutdoorCategoryName] = {};
          }

          // Copy all subcategories from regular Outdoor to VSH Outdoor
          regularOutdoorSubcategories.forEach((subcategoryName) => {
            // Only add subcategories that don't already exist
            if (!photoGroups[vshOutdoorCategoryName][subcategoryName]) {
              photoGroups[vshOutdoorCategoryName][subcategoryName] = [];
            }
          });
        } else {
          // If regular Outdoor has no subcategories, remove VSH Outdoor category
          if (photoGroups[vshOutdoorCategoryName]) {
            delete photoGroups[vshOutdoorCategoryName];
            // Remove from allCategoryNames if it exists
            allCategoryNames.delete(vshOutdoorCategoryName);
          }
        }
      }

      setPhotosByCategory(photoGroups);

      if (data.details?.business?.slug) {
        setVenueSlug(data.details.business.slug);
      }
    };

    enrichAndGroupPhotos(activeTab);
  }, [data, sort, activeTab, defaultTabSet]);

  // If walk-through and user photos are absent but experience photos exist, default to experiences tab
  useEffect(() => {
    if (defaultTabSet && !hasSociabilityPhotos && !hasUserPhotos && hasExperiencePhotos) {
      setActiveTab("experiences");
    }
  }, [defaultTabSet, hasSociabilityPhotos, hasUserPhotos, hasExperiencePhotos]);

  // Scroll spy - auto-select category chip when scrolling through sections
  useEffect(() => {
    if (Object.keys(photosByCategory).length === 0) return;

    // Find the scrollable parent container
    const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;
      
      let current = element.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        
        if (overflowY === 'auto' || overflowY === 'scroll' || 
            overflowX === 'auto' || overflowX === 'scroll') {
          return current;
        }
        
        current = current.parentElement;
      }
      
      return null;
    };

    const scrollContainer = containerRef.current 
      ? findScrollableParent(containerRef.current) 
      : null;
    
    if (!scrollContainer) {
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Don't update during programmatic scrolls
      if (isProgrammaticScroll.current) {
        return;
      }

      // Debounce scroll updates to prevent jitter
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Find which section has the most visible content in the viewport
        const headerOffset = 100; // Account for sticky header
        const viewportHeight = window.innerHeight;
        let mostVisibleSection = "";
        let maxVisibleArea = 0;

        for (const [categoryName, ref] of Object.entries(categoryRefs.current)) {
          if (ref) {
            const rect = ref.getBoundingClientRect();
            
            // Calculate how much of this section is visible in the viewport
            // Account for the sticky header by treating anything above headerOffset as "not visible"
            const visibleTop = Math.max(rect.top, headerOffset);
            const visibleBottom = Math.min(rect.bottom, viewportHeight);
            const visibleArea = Math.max(0, visibleBottom - visibleTop);
            
            // Track which section has the most visible content
            if (visibleArea > maxVisibleArea) {
              maxVisibleArea = visibleArea;
              mostVisibleSection = categoryName;
            }
          }
        }

        if (mostVisibleSection && mostVisibleSection !== activeFilter) {
          // Only allow scroll detection to change filter if it wasn't set by cid or tid
          if (
            (!cidSetFilter || mostVisibleSection === cidSetFilter) &&
            (!tidSetFilter || mostVisibleSection === cidSetFilter)
          ) {
            setActiveFilter(mostVisibleSection);
          }
        }
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [photosByCategory, activeFilter, cidSetFilter, tidSetFilter]);

  // Set the header height for sidebar positioning
  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerElement = document.querySelector(".header-container");
      if (headerElement) {
        const headerHeight = headerElement.getBoundingClientRect().height;
        document.documentElement.style.setProperty(
          "--header-height",
          `${headerHeight}px`,
        );
      }
    };

    // Update on load and resize
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    // Update when data loads (which might change header size)
    if (!isLoading && reviewPhotos.length > 0) {
      setTimeout(updateHeaderHeight, 100);
    }

    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, [isLoading, reviewPhotos.length, tidSetFilter]);

  const handleFlagVenuePhotoClose = () => {
    setIsFlagVenuePhotoOpen(false);
  };

  const handleOpenFullImage = (image) => {
    setCurrentImage(image);
    setFullImageView(true);
    // Remove focus from the button to prevent blue border after closing modal
    (document.activeElement as HTMLElement)?.blur();
    // photo_lightbox_opened is now fired centrally on mount inside FullImageViewer.
  };

  // walkthrough_tab / by_users_tab -> photo_collection_view_changed (the photo-type tabs).
  const selectPhotoCollection = (tab: string) => {
    if (tab !== activeTab) {
      trackEvent("photo_collection_view_changed", {
        business_id: data?.details?.business?.id?.toString() ?? "",
        photo_collection_type: PHOTO_COLLECTION_TYPE_BY_TAB[tab] ?? tab,
        photo_index: 0,
        place_name: data?.details?.name ?? "",
        previous_photo_collection_type:
          PHOTO_COLLECTION_TYPE_BY_TAB[activeTab] ?? activeTab,
      });
    }
    setActiveTab(tab);
  };

  const handleFlagPhoto = (image) => {
    if (!user || !isLoggedIn) {
      setActionToPerformAfterLogin("flagPhoto");
      setActionToPerformValueAfterLogin(image);
      setActionToPerformOwner(FLAG_OWNER);
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setSelectedImage(image);
    setIsFlagVenuePhotoOpen(true);
  };

  // Scroll spy â€” auto-select category tab when scrolling through sections
  useEffect(() => {
    if (Object.keys(categoryRefs.current).length === 0) return;

    const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;
      let current = element.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        if (style.overflowY === "auto" || style.overflowY === "scroll") return current;
        current = current.parentElement;
      }
      return null;
    };

    const scrollContainer = containerRef.current
      ? findScrollableParent(containerRef.current)
      : null;
    if (!scrollContainer) return;

    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const navHeight = navRef.current ? navRef.current.offsetHeight : 64;
        const viewportHeight = window.innerHeight;
        let activeSection = "";
        let lastAboveNav = "";

        for (const [catName, ref] of Object.entries(categoryRefs.current)) {
          if (!ref) continue;
          const rect = ref.getBoundingClientRect();
          if (rect.top < navHeight) {
            lastAboveNav = catName;
          } else if (rect.top < viewportHeight) {
            activeSection = catName;
            break;
          } else {
            break;
          }
        }

        if (!activeSection) activeSection = lastAboveNav;
        if (!activeSection) {
          const firstKey = Object.keys(categoryRefs.current)[0];
          if (firstKey) activeSection = firstKey;
        }
        if (activeSection) setActiveFilter(activeSection);
      }, 100);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [photosByCategory]);

  const renderFilterTabs = () => {
    // Filter categories to only show those that have subcategories and photos
    const categoriesWithContent = Object.entries(photosByCategory)
      .filter(([, subcategories]) => {
        // CheckIcon if category has subcategories
        const subcategoryEntries = Object.entries(subcategories);
        if (subcategoryEntries.length === 0) return false;

        // CheckIcon if any subcategory has photos
        return subcategoryEntries.some(([, photos]) => photos.length > 0);
      })
      .map(([categoryName]) => categoryName);

    return (
      <nav
        ref={navRef}
        className="sticky top-0 z-10 bg-bg-brand-secondary-default -mx-4 px-4 md:mx-0 md:px-0 md:-mt-4 md:pt-4"
        aria-label="Photo categories"
      >
        {/* UserIcon type tabs row - By Sociability / By UsersIcon */}
        {(hasSociabilityPhotos || hasUserPhotos) && (
          <div className="border-b border-b-primary-150 relative overflow-hidden">
            <div className="flex gap-0 overflow-x-auto overflow-y-hidden items-center h-[52px]">
                <div className="flex gap-0 flex-1 h-full">
                  {hasSociabilityPhotos && (
                    <button
                      type="button"
                      aria-pressed={activeTab === "by-sociability"}
                      onClick={() => selectPhotoCollection("by-sociability")}
                      className={`${
                        activeTab === "by-sociability"
                          ? "label-m-semibold text-fg-brand-subtle relative"
                          : "label-m-medium text-fg-neutral-subtle"
                      } bg-transparent rounded-none px-3 h-full flex items-center whitespace-nowrap`}
                    >
                      {i18n.get("bySociability")} ({sociabilityPhotoCount})
                      {activeTab === "by-sociability" && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-bg-brand-primary-default" />
                      )}
                    </button>
                  )}
                  {hasUserPhotos && (
                    <button
                      type="button"
                      aria-pressed={activeTab === "by-users"}
                      onClick={() => selectPhotoCollection("by-users")}
                      className={`${
                        activeTab === "by-users"
                       ? "label-m-semibold text-fg-brand-subtle relative"
                          : "label-m-medium text-fg-neutral-subtle"
                      } bg-transparent rounded-none px-3 h-full flex items-center whitespace-nowrap`}
                    >
                      {i18n.get("byUsers")} ({userPhotoCount})
                      {activeTab === "by-users" && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-bg-brand-primary-default" />
                      )}
                    </button>
                  )}
                  {hasExperiencePhotos && (
                    <button
                      type="button"
                      aria-pressed={activeTab === "experiences"}
                      onClick={() => selectPhotoCollection("experiences")}
                      className={`${
                        activeTab === "experiences"
                          ? "label-m-semibold text-fg-brand-subtle relative"
                          : "label-m-medium text-fg-neutral-subtle"
                      } bg-transparent rounded-none px-3 h-full flex items-center whitespace-nowrap`}
                    >
                      {i18n.get("experiences")} ({experiencePhotosList.length})
                      {activeTab === "experiences" && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-bg-brand-primary-default" />
                      )}
                    </button>
                  )}
                </div>

                {/* View All Photos Icon Button */}
                {onShowPhotosDetailView && (
                  <button
                    type="button"
                    onClick={() => {
                      // Open the photos detail view
                      onShowPhotosDetailView(null);

                      // photo_gallery_expand_action_button -> photo_gallery_viewed.
                      trackEvent("photo_gallery_viewed", {
                        business_id:
                          data?.details?.business?.id?.toString() ?? "",
                        entry_page: getCurrentPageKey(),
                        entry_page_url: window.location.href,
                        photos_count:
                          sociabilityPhotoCount +
                          userPhotoCount +
                          experiencePhotosList.length,
                        place_header_name: "",
                        place_name: data?.details?.name ?? "",
                      });
                    }}
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg hover:bg-bg-brand-secondary-hover transition-colors"
                    aria-label="View all photos"
                    title="View all photos"
                  >
                    <Maximize2 size={20} className="text-fg-brand-subtle" />
                  </button>
                )}
            </div>
          </div>
        )}

        {/* Category tabs row — hidden for Experiences tab */}
        {activeTab !== "experiences" && <div role="tablist" aria-orientation="horizontal" aria-label="Photo categories">
        <ScrollContainer className="flex gap-1 items-center py-3  overflow-x-auto">
            {categoriesWithContent.map((categoryName) => (
              <button
                key={categoryName}
                type="button"
                role="tab"
                className={cn(
                  "flex-shrink-0 min-w-[44px] min-h-[40px] px-3 py-2 rounded-lg whitespace-nowrap transition-colors",
                  activeFilter === categoryName
                    ? "label-m-semibold bg-bg-brand-tertiary-default border border-border-token-brand-subtle text-fg-brand-subtle"
                    : "label-m-medium bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover text-fg-neutral-subtle",
                )}               
                onClick={() => {
                  setActiveFilter(categoryName);
                  // Scroll to category section using the new scroll function
                  // Use requestAnimationFrame for better timing and smoother scrolling
                  requestAnimationFrame(() => {
                    setTimeout(() => scrollToCategory(categoryName), 50);
                  });
                }}
                aria-selected={activeFilter === categoryName}
              >
                {categoryName}
              </button>
            ))}
        </ScrollContainer>
        </div>}
      </nav>
    );
  };

  const renderPhotoGrid = () => {
    // Always show all categories
    const categoriesToShow = Object.entries(photosByCategory);

    return (
      <div className="space-y-8">
        {categoriesToShow.map(([categoryName, subcategories]) => {
          const subcategoryCount = Object.keys(subcategories).length;
          const totalPhotoCount = Object.values(subcategories).reduce(
            (sum, photos) => sum + photos.length,
            0,
          );

          // Initialize category refs if not already done
          if (!subcategoryRefs.current[categoryName]) {
            subcategoryRefs.current[categoryName] = {};
          }

          return (
            <div
              key={categoryName}
              data-category-section={categoryName}
              className="mb-8"
              ref={(el) => {
                if (el) {
                  categoryRefs.current[categoryName] = el;
                }
              }}
            >
              {subcategoryCount > 0 && (
                <div className="">
                  {/* Title row */}
                  <h2
                    className="title-s-semibold text-fg-neutral-strong flex items-center gap-2"
                    aria-label={`${categoryName} ${subcategoryCount} areas, ${totalPhotoCount} photo${totalPhotoCount !== 1 ? "s" : ""}`}
                    data-category-heading={categoryName}
                    tabIndex={-1}
                  >
                    {categoryName}
                    <span className="title-s-semibold text-fg-neutral-strong flex items-center gap-2">
                      ({totalPhotoCount})
                    </span>
                  </h2>
                </div>
              )}

              <div className="gap-2">
                {Object.entries(subcategories).map(
                  ([subcategoryName, photos]) => (
                    <div
                      key={subcategoryName}
                      className="scroll-mt-20"
                      ref={(el) => {
                        if (el) {
                          subcategoryRefs.current[categoryName][
                            subcategoryName
                          ] = el;
                        }
                      }}
                    >
                      <div className="py-4">
                        <h3
                          className="body-base-medium text-fg-neutral-strong"
                          data-subcategory-heading={`${categoryName}:${subcategoryName}`}
                        >
                          {subcategoryName} •{" "}
                          {photos.length > 0
                            ? `${photos.length} photo${photos.length !== 1 ? "s" : ""}`
                            : "No photos"}
                        </h3>
                      </div>
                      {photos.length > 0 && (
                        <div
                          className="photo-grid-container relative"
                          data-category={categoryName}
                          data-subcategory={subcategoryName}
                        >
                          <RowsPhotoAlbum
                            photos={photos.map((image) => ({
                              src: bucketUrlPrefix + image.path,
                              width: image.width,
                              height: image.height,
                              alt: image.alt_text || "",
                            }))}
                            spacing={8}
                            targetRowHeight={120}
                            // Use a function to determine max photos based on container width
                            rowConstraints={(containerWidth) => {
                              // For mobile screens (< 768px): 2 photos
                              // For iPad/tablet screens (768px - 1024px): 3 photos
                              // For desktop screens (> 1024px): 4 photos
                              let maxPhotos = 2; // Default for mobile
                              if (containerWidth > 768) {
                                maxPhotos = 3; // iPad
                              }
                              if (containerWidth > 1024) {
                                maxPhotos = 4; // Desktop
                              }
                              return {
                                maxPhotos,
                                minPhotos: 1,
                                singleRowMaxHeight: 350,
                              };
                            }}
                            onClick={({ index }) =>
                              handleOpenFullImage(photos[index])
                            }
                            render={{
                              button: (props, context) => {
                                const { index } = context;
                                const currentPhoto = photos[index];
                                // Initialize refs for this category/subcategory if not exists
                                if (!photoGridRefs.current[categoryName]) {
                                  photoGridRefs.current[categoryName] = {};
                                }

                                // Create a more descriptive aria-label that includes alt text
                                const altText = currentPhoto.alt_text || "";
                                const ariaLabel = altText
                                  ? `Photo ${index + 1} of ${photos.length} in ${subcategoryName}. ${altText}`
                                  : `Photo ${index + 1} of ${photos.length} in ${subcategoryName}`;

                                return (
                                  <button
                                    {...props}
                                    ref={
                                      index === 0
                                        ? (el) => {
                                            if (el) {
                                              photoGridRefs.current[
                                                categoryName
                                              ][subcategoryName] = el;
                                            }
                                          }
                                        : undefined
                                    }
                                    aria-label={ariaLabel}
                                    className={`${props.className} rounded-lg relative overflow-hidden group`}
                                    style={{
                                      ...props.style,
                                    }}
                                    onKeyDown={(e) => {
                                      // Handle both Enter/Space for opening photo and Tab for navigation
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenFullImage(photos[index]);
                                      }
                                    }}
                                  >
                                    {props.children}
                                    <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded-lg" />
                                  </button>
                                );
                              },
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderExperiencePhotosGrid = () => {
    if (experiencePhotosList.length === 0) {
      return (
        <div className="flex flex-1 flex-col justify-start sm:justify-center items-center px-4 pt-8 sm:pt-0">
          <FolderDottedIcon size={32} className="text-fg-neutral-strong" />
          <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
            {i18n.get("noPhotosForThisVenueYet")}
          </p>
        </div>
      );
    }

    return (
      <div className="photo-grid-container relative pt-3">
        <RowsPhotoAlbum
          photos={experiencePhotosList.map((photo) => ({
            src: bucketUrlPrefix + photo.path,
            width: photo.width ?? 1200,
            height: photo.height ?? 900,
            alt: photo.alt_text || "",
          }))}
          spacing={8}
          targetRowHeight={120}
          rowConstraints={(containerWidth) => {
            let maxPhotos = 2;
            if (containerWidth > 768) maxPhotos = 3;
            if (containerWidth > 1024) maxPhotos = 4;
            return { maxPhotos, minPhotos: 1, singleRowMaxHeight: 350 };
          }}
          onClick={({ index }) => {
            setCurrentExperienceViewerIndex(index);
            setIsExperienceViewerOpen(true);
          }}
          render={{
            button: (props, context) => {
              const { index } = context;
              const photo = experiencePhotosList[index];
              const altText = photo?.alt_text || "";
              const ariaLabel = altText
                ? `Experience photo ${index + 1} of ${experiencePhotosList.length}. ${altText}`
                : `Experience photo ${index + 1} of ${experiencePhotosList.length}`;
              return (
                <button
                  {...props}
                  aria-label={ariaLabel}
                  className={`${props.className} rounded-lg relative overflow-hidden group`}
                  style={{ ...props.style }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setCurrentExperienceViewerIndex(index);
                      setIsExperienceViewerOpen(true);
                    }
                  }}
                >
                  {props.children}
                  <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded-lg" />
                </button>
              );
            },
          }}
        />
      </div>
    );
  };

  return (
    <>
      {/* Scroll spy live announcement for screen readers */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {activeFilter ? `${activeFilter} photos` : ""}
      </div>

      <Helmet>
        <title>
          {data?.details?.name
            ? `${data.details.name.length > 30 ? data.details.name.substring(0, 30) + "..." : data.details.name}`
            : ""}
          | Photos | Sociability
        </title>
      </Helmet>

      {!doNotShowBackButton && (
        <div className="max-w-[1160px] mx-auto pb-0 md:pb-2 header-container md:mt-2">
          {/* Header with back button */}
          <section className="flex items-center py-1 md:py-2" role="none">
            {mode === "nearby" ? (
              <button
                className="flex flex-row items-center"
                onClick={() => {
                  window.history.back();
                }}
              >
                <ArrowLeftIcon className="mr-1" aria-hidden="true" />
                <span className="text-fg-neutral-strong label-m-semibold">
                  {`${i18n.get("backToVenueDetails")}`}
                </span>
              </button>
            ) : (
              <Link
                className="flex flex-row items-center"
                to={`/venuedetails/${venueSlug}`}
                role="button"
              >
                <ArrowLeftIcon className="mr-1" aria-hidden="true" />
                <span className="text-fg-neutral-strong label-m-semibold">
                  {`${i18n.get("backToVenueDetails")}`}
                </span>
              </Link>
            )}
          </section>
        </div>
      )}

      {/* Main content area */}
      {!isLoading &&
        (reviewPhotos.length > 0 ||
          (!hasSociabilityPhotos && !hasUserPhotos)) && (
          <div className="flex flex-col w-full">
            {/* Title section */}
            {!doNotShowBackButton && (
              <div className="py-3">
                <h1 className="label-l-bold text-fg-neutral-strong">
                  {i18n.get("photoGallery")}
                </h1>
              </div>
            )}

            {/* Category Navigation Tabs */}
            {!isLoading && reviewPhotos.length > 0 && renderFilterTabs()}

            {/* Content Area */}
            <div 
              ref={containerRef}
              className={`flex flex-col pt-3 ${reviewPhotos.length > 0 || (activeTab === "experiences" && experiencePhotosList.length > 0) ? "pb-[100vh]" : ""} w-full min-w-0 min-h-[calc(100vh-200px)]`}
            >
              {/* Experience photos grid */}
              {!isLoading && activeTab === "experiences" && renderExperiencePhotosGrid()}

              {/* Regular photo grid (Walk-through / By Users) */}
              {!isLoading && activeTab !== "experiences" && reviewPhotos.length > 0 && renderPhotoGrid()}

              {/* Empty State for Walk-through / By Users */}
              {!isLoading && activeTab !== "experiences" && reviewPhotos?.length === 0 && (
                <div className="flex flex-1 flex-col justify-start sm:justify-center items-center px-4 pt-8 sm:pt-0">
                  <FolderDottedIcon size={32} className="text-fg-neutral-strong" />
                  <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
                    {i18n.get("noPhotosForThisVenueYet")}
                  </p>
                </div>
              )}

              {/* Loading state */}
              {isLoading && <Loader />}
            </div>
          </div>
        )}

      <FlagVenuePhoto
        photoReviewId={selectedImage?.review_photos_id}
        venueName={data?.details?.name}
        isOpen={isFlagVenuePhotoOpen}
        onClose={() => handleFlagVenuePhotoClose()}
        venueDetails={{
          data: {
            id: data?.details?.id,
            brandId: data?.details?.brandId,
            brandName: data?.details?.brandName,
            address: data?.details?.address,
            name: data?.details?.name,
            types: data?.details?.types,
            business_photos: data?.details?.businessPhotos,
          },
        }}
      />

      {/* Full image view - rendered using portal for full page coverage */}
      {fullImageView &&
        currentImage &&
        portalRoot &&
        createPortal(
          <FullImageViewer
            images={reviewPhotos.map((photo) => ({
              src: bucketUrlPrefix + photo.path,
              alt: photo.alt_text || "",
              tag_name: photo.tag_name,
              category_name: photo.category_name,
              created_at: photo.created_at,
              photo_type: photo.photo_type,
              review_photos_id: photo.review_photos_id,
              width: photo.width,
              height: photo.height,
              sort_order: photo.sort_order,
              qtags: photo.qtags || [],
              tags: photo.tags || [
                {
                  tagName: photo.tag_name || "Uncategorized",
                  categoryDisplaySectionId: "",
                },
              ],
              // Use the display sections from the photo
              displaySections: photo.displaySections,
              // Add venue details for tracking
              venueId: data?.details?.id,
              venueName: data?.details?.name,
              venueAddress: data?.details?.address,
              venueBrandId: data?.details?.brandId,
              venueBrandName: data?.details?.brandName,
              venueTypes: data?.details?.types,
              user_roles: photo.user_roles,
            }))}
            currentIndex={reviewPhotos.findIndex((p) => p === currentImage)}
            onClose={() => setFullImageView(false)}
            onFlag={handleFlagPhoto}
            showFlag={true}
            showRightTab={true}
          />,
          portalRoot,
        )}

      {/* Experience photos full image viewer */}
      {isExperienceViewerOpen &&
        experiencePhotosList.length > 0 &&
        portalRoot &&
        createPortal(
          <FullImageViewer
            images={experiencePhotosList.map((photo) => ({
              src: bucketUrlPrefix + photo.path,
              alt: photo.alt_text || "",
              tag_name:
                photo.review?.accessibility_experience === "cool"
                  ? "Sweet experience"
                  : "Sour experience",
              category_name: "Experience",
              created_at: photo.created_at || photo.createdAt || "",
              photo_type: "experience",
              review_photos_id: photo.id?.toString(),
              width: photo.width || 1200,
              height: photo.height || 900,
              sort_order: 0,
              qtags: [],
              tags: [],
              displaySections: {},
              venueId: data?.details?.id,
              venueName: data?.details?.name,
              venueAddress: data?.details?.address,
              venueBrandId: data?.details?.brandId,
              venueBrandName: data?.details?.brandName,
              venueTypes: data?.details?.types,
              user_roles: { isGeneralUser: true },
              // Experience review fields for the Review tab
              isExperiencePhoto: true,
              review_user_first_name: photo.review?.userFirstName,
              review_user_last_name: photo.review?.userLastName,
              review_user_profile_photo: photo.review?.userProfilePhoto
                ? bucketUrlPrefix + photo.review.userProfilePhoto
                : undefined,
              review_user_color: photo.review?.userProfileColorCode,
              review_notes: photo.review?.notes,
              review_time_ago: photo.review?.timeAgo,
              review_accessibility_experience: photo.review?.accessibility_experience,
            }))}
            currentIndex={currentExperienceViewerIndex}
            onClose={() => setIsExperienceViewerOpen(false)}
            onFlag={handleFlagPhoto}
            showFlag={true}
            showRightTab={true}
          />,
          portalRoot,
        )}
    </>
  );
}

export default VenuePhotosContent;
